import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CategoryReportItem } from '../../types';

interface CategoryReportProps {
  categories: CategoryReportItem[];
  grandTotal: number;
  totalExpenses: number;
  projectName?: string;
  startDate?: string;
  endDate?: string;
}

const COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
];

const CategoryReport: React.FC<CategoryReportProps> = ({
  categories,
  grandTotal,
  totalExpenses,
  projectName,
  startDate,
  endDate,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(selectedCategory === categoryId ? null : categoryId);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(1)}%`;
  };

  // Prepare data for charts
  const categoryChartData = categories.map((cat) => ({
    name: cat.categoryName,
    amount: cat.totalAmount,
    percentage: cat.percentage,
  }));

  const selectedCategoryData = selectedCategory
    ? categories.find((cat) => cat.categoryId === selectedCategory)
    : null;

  const subcategoryChartData = selectedCategoryData
    ? selectedCategoryData.subcategoryReports.map((sub) => ({
        name: sub.subcategoryName,
        amount: sub.totalAmount,
        percentage: sub.percentage,
      }))
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Reporte por Categoría
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          {projectName && (
            <div>
              <span className="text-gray-600">Proyecto:</span>
              <span className="ml-2 font-semibold">{projectName}</span>
            </div>
          )}
          {startDate && endDate && (
            <div>
              <span className="text-gray-600">Período:</span>
              <span className="ml-2 font-semibold">
                {startDate} - {endDate}
              </span>
            </div>
          )}
          <div>
            <span className="text-gray-600">Total Gastos:</span>
            <span className="ml-2 font-semibold">{totalExpenses}</span>
          </div>
          <div>
            <span className="text-gray-600">Monto Total:</span>
            <span className="ml-2 font-semibold text-blue-600">
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - Categories */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Distribución por Categoría</h3>
          <div className="h-64 lg:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={categoryChartData}
                margin={{ top: 5, right: 20, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  className="text-xs lg:text-sm"
                />
                <YAxis className="text-xs lg:text-sm" />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  labelStyle={{ color: '#000' }}
                />
                <Bar
                  dataKey="amount"
                  fill="#3b82f6"
                  onClick={(data) => handleCategoryClick(
                    categories.find(c => c.categoryName === data.name)?.categoryId || ''
                  )}
                  cursor="pointer"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart - Categories */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Porcentaje por Categoría</h3>
          <div className="h-64 lg:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryChartData}
                  dataKey="amount"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry) => `${entry.name}: ${formatPercentage(entry.percentage)}`}
                  labelLine={false}
                  className="text-xs"
                >
                  {categoryChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                      onClick={() => handleCategoryClick(
                        categories.find(c => c.categoryName === entry.name)?.categoryId || ''
                      )}
                      cursor="pointer"
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Subcategory Drill-down */}
      {selectedCategoryData && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              Subcategorías de {selectedCategoryData.categoryName}
            </h3>
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Cerrar
            </button>
          </div>
          <div className="h-64 lg:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={subcategoryChartData}
                margin={{ top: 5, right: 20, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  className="text-xs lg:text-sm"
                />
                <YAxis className="text-xs lg:text-sm" />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                  labelStyle={{ color: '#000' }}
                />
                <Bar dataKey="amount" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Detailed Table - Desktop */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gastos
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  %
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <React.Fragment key={category.categoryId}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {category.categoryName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {category.expenseCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                      {formatCurrency(category.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatPercentage(category.percentage)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => toggleCategory(category.categoryId)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {expandedCategories.has(category.categoryId)
                          ? 'Ocultar'
                          : 'Ver Subcategorías'}
                      </button>
                    </td>
                  </tr>
                  {expandedCategories.has(category.categoryId) &&
                    category.subcategoryReports.map((subcategory) => (
                      <tr key={subcategory.subcategoryId} className="bg-gray-50">
                        <td className="px-6 py-3 whitespace-nowrap pl-12">
                          <div className="text-sm text-gray-700">
                            └─ {subcategory.subcategoryName}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                          {subcategory.expenseCount}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                          {formatCurrency(subcategory.totalAmount)}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                          {formatPercentage(subcategory.percentage)}
                        </td>
                        <td className="px-6 py-3"></td>
                      </tr>
                    ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="bg-gray-100">
              <tr>
                <td className="px-6 py-4 font-bold text-gray-900">Total</td>
                <td className="px-6 py-4 text-right font-bold text-gray-900">
                  {totalExpenses}
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  {formatCurrency(grandTotal)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-gray-900">
                  100%
                </td>
                <td className="px-6 py-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden p-4 space-y-4">
          {categories.map((category) => (
            <div key={category.categoryId} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="font-semibold text-gray-900">
                  {category.categoryName}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">
                    {formatCurrency(category.totalAmount)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatPercentage(category.percentage)}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-3">
                {category.expenseCount} gastos
              </div>
              <button
                onClick={() => toggleCategory(category.categoryId)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {expandedCategories.has(category.categoryId)
                  ? 'Ocultar Subcategorías'
                  : 'Ver Subcategorías'}
              </button>
              {expandedCategories.has(category.categoryId) && (
                <div className="mt-3 pl-4 space-y-2 border-l-2 border-gray-200">
                  {category.subcategoryReports.map((subcategory) => (
                    <div
                      key={subcategory.subcategoryId}
                      className="flex justify-between items-center text-sm"
                    >
                      <span className="text-gray-700">
                        {subcategory.subcategoryName}
                      </span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(subcategory.totalAmount)}
                        </div>
                        <div className="text-xs text-gray-600">
                          {subcategory.expenseCount} gastos
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="border-t-2 border-gray-300 pt-4 flex justify-between items-center font-bold">
            <span className="text-gray-900">Total</span>
            <div className="text-right">
              <div className="text-lg text-blue-600">
                {formatCurrency(grandTotal)}
              </div>
              <div className="text-sm text-gray-600">{totalExpenses} gastos</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryReport;
